import { FlattenedShorcut, ShortcutGroupToShortcuts } from "modules/shortcuts/models";
import React, { MouseEvent, useRef, useState } from "react";
import DraggableFlatShortcutsFormat from "./draggableFlatShortcutsFormat";
import DraggableShortcutGroupsFormat from "./draggableShortcutGroupsFormat";

import "../../styles/shortcutsFormatEditor.sass";

interface IShortcutsFormatEditorProps {
    newShortcutFormat: ShortcutGroupToShortcuts[];
    setNewShortcutFormat: React.Dispatch<React.SetStateAction<ShortcutGroupToShortcuts[]>>
}

const ShortcutsFormatEditor: React.FC<IShortcutsFormatEditorProps> = ({
    newShortcutFormat,
    setNewShortcutFormat
}) => {
    const [draggedGroupIdx, setDraggedGroupIdx] = useState<number | null>(null);
    const scrollingContainerRef = useRef<HTMLDivElement>(null);
    const isScrolling = useRef(false);

    if (!newShortcutFormat) return <></>;
    
    const starScrolling = (container: HTMLDivElement, direction: "up" | "down") => {
        let speed = 1;
    
        const step = () => {
            if (!isScrolling.current) return;
        
            if (direction === "down" && container.scrollTop + container.clientHeight < container.scrollHeight) {
                container.scrollTop += speed;
                requestAnimationFrame(step);
            } 
            else if (direction === "up" && container.scrollTop > 0) {
                container.scrollTop -= speed;
                requestAnimationFrame(step);
            }
        };
    
        requestAnimationFrame(step);
    };

    const shortcutGroups = newShortcutFormat.map(sgts => sgts.shortcutGroup);
    const flattenedShortcutFormat: FlattenedShorcut[] = newShortcutFormat.flatMap(sgts => [
        {
            isGroup: true,
            ...sgts.shortcutGroup
        },
        ...sgts.shortcuts.map(s => ({
            isGroup: false,
            ...s
        }))
    ]);

    const onMouseMove = (event: MouseEvent, isDragging: boolean) => {
        if (!isDragging || !scrollingContainerRef.current) return;
    
        const container = scrollingContainerRef.current;
        const { clientHeight } = container;
        const bottomThreshold = clientHeight * 0.8;
        const topThreshold = clientHeight * 0.2;
        const mouseY = event.clientY - container.getBoundingClientRect().top;
    
        if (mouseY > bottomThreshold) {
            isScrolling.current = true;
            starScrolling(container, "down");
        } 
        else if (mouseY < topThreshold) {
            isScrolling.current = true;
            starScrolling(container, "up");
        }
        else {
            isScrolling.current = false;
        }
    };

    const onGroupMouseDown = (groupId: string) => setDraggedGroupIdx(shortcutGroups.findIndex(sg => sg.id === groupId));

    const onShortcutDrop = (itemToMoveIdx: number, moveToItemId?: string, moveToItemIdx?: number) => {
        // if the move to index is the same as the original, don't process anything
        if (itemToMoveIdx === moveToItemIdx) return;

        const flattenedShortcutToMoveCopy = {...flattenedShortcutFormat[itemToMoveIdx]};

        let flattenedFormatCopy = [...flattenedShortcutFormat];

        // remove the shortcut
        flattenedFormatCopy.splice(itemToMoveIdx, 1);
        
        // case where we move the shortcut to the very end of the list
        if (moveToItemId === undefined && moveToItemIdx! >= flattenedShortcutFormat.length) {
            flattenedFormatCopy.push(flattenedShortcutToMoveCopy);
        }
        else if (moveToItemId) {
            // find the new index of the shortcut 
            const moveToIdx = flattenedFormatCopy.findIndex(f => f.id === moveToItemId);

            // insert the removed shortcutgroup into the new space
            flattenedFormatCopy.splice(moveToIdx, 0, flattenedShortcutToMoveCopy);
        }

        // transform the flattened format copy back into the original format
        let newFormat: ShortcutGroupToShortcuts[] = [];

        for (const flattenedShortcut of flattenedFormatCopy) {
            if (flattenedShortcut.isGroup) {
                newFormat.push({shortcutGroup: {id: flattenedShortcut.id, name: flattenedShortcut.name}, shortcuts: []})
            }
            else {
                const shortcut = {
                    id: flattenedShortcut.id,
                    name: flattenedShortcut.name,
                    audiences: flattenedShortcut.audiences ?? [],
                    isEnabled: flattenedShortcut.isEnabled ?? false,
                    url: flattenedShortcut.url ?? "",
                    openInNewWindow: flattenedShortcut.openInNewWindow ?? true
                };

                newFormat[newFormat.length - 1].shortcuts.push(shortcut);
            }
        }

        setNewShortcutFormat(newFormat);
    }

    const onShortcutGroupDrop = (itemToMoveIdx: number, moveToItemId?: string, moveToItemIdx?: number) => {
        setDraggedGroupIdx(null);

        // if the move to index is the same as the original, don't process anything
        if (itemToMoveIdx === moveToItemIdx) return;

        const shortcutGroupToMoveCopy = {...newShortcutFormat[itemToMoveIdx]};
        let shortcutFormatCopy = [...newShortcutFormat.map(sgts => ({...sgts}))];

        // remove the shortcut group
        shortcutFormatCopy.splice(itemToMoveIdx, 1);

        // if the moveTo index is greater than the whole list, it means we're appending it to the end of the list.
        if (moveToItemId === undefined && moveToItemIdx! >= newShortcutFormat.length) {
            // append to the end of the list
            shortcutFormatCopy.push(shortcutGroupToMoveCopy);
        }
        else if (moveToItemId) {
            // find the new index of the shortcut group
            const moveToIdx = shortcutFormatCopy.findIndex(sgts => sgts.shortcutGroup.id === moveToItemId);

            // insert the removed shortcutgroup into the new space
            shortcutFormatCopy.splice(moveToIdx, 0, shortcutGroupToMoveCopy);
        }
        setNewShortcutFormat(shortcutFormatCopy);
    }

    return <div 
        className="shortcuts-editor-container" 
        ref={scrollingContainerRef}
    >
        <div className="shortcut-group-list">
            {
                draggedGroupIdx === null 
                    ? <DraggableFlatShortcutsFormat
                        flattenedFormat={flattenedShortcutFormat}
                        onDrop={onShortcutDrop}
                        onGroupMouseDown={onGroupMouseDown}
                        onMouseMove={onMouseMove}
                    />
                    : <DraggableShortcutGroupsFormat
                        shortcutGroups={shortcutGroups}
                        onDrop={onShortcutGroupDrop}
                        shortcutGroupDragged={draggedGroupIdx}
                        onMouseMove={onMouseMove}
                    />
            }
        </div>
    </div>;
};

export default ShortcutsFormatEditor;
